import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Checkbox from '@amzn/meridian/checkbox';
import { adminApi, UserRecord } from '../service/adminApiService';
import { SearchInput } from '../components/SearchInput';
import { FilterBar } from '../components/FilterBar';
import { PaginationControls } from '../components/PaginationControls';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

export const UsersListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const [items, setItems] = useState<UserRecord[]>([]);
  const [tokens, setTokens] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const search = params.get('search') || '';
  const group = params.get('group') || '';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
      setPageIndex(0);
      setTokens([null]);
      setSelected(new Set());
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams({}, { replace: true });
    setPageIndex(0);
    setTokens([null]);
    setSelected(new Set());
  }, [setParams]);

  const fetchPage = useCallback(
    async (tokenIndex: number) => {
      setLoading(true);
      setError(null);
      try {
        const token = tokens[tokenIndex] || undefined;
        const response = await adminApi.listUsers({
          next_token: token,
          search: search || undefined,
          group: group || undefined,
        });
        setItems(response.items);
        setNextToken(response.next_token);
      } catch (err: any) {
        setError(err?.message || 'Failed to load users');
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [tokens, search, group],
  );

  useEffect(() => {
    fetchPage(pageIndex);
  }, [fetchPage, pageIndex]);

  const activeCount = useMemo(
    () => [search, group].filter((v) => v && v.length > 0).length,
    [search, group],
  );

  const toggleSelected = (alias: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(alias)) next.delete(alias);
      else next.add(alias);
      return next;
    });
  };

  const performDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await adminApi.deleteUser(pendingDelete);
      toast.push({ type: 'success', message: `Deleted user mapping ${pendingDelete}` });
      setPendingDelete(null);
      fetchPage(pageIndex);
    } catch (err: any) {
      toast.push({ type: 'error', message: err?.message || 'Delete failed' });
    } finally {
      setBusy(false);
    }
  };

  const performBulkDelete = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const aliases = Array.from(selected);
      await adminApi.bulkDeleteUsers({ user_aliases: aliases });
      toast.push({
        type: 'success',
        message: `Removed ${aliases.length} user mapping(s)`,
      });
      setSelected(new Set());
      setPendingBulkDelete(false);
      fetchPage(pageIndex);
    } catch (err: any) {
      toast.push({ type: 'error', message: err?.message || 'Bulk delete failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Column spacing="large">
      <PageHeader
        title="Users"
        subtitle={`${items.length} user${items.length === 1 ? '' : 's'} shown`}
        actions={
          <>
            <Button
              type="tertiary"
              onClick={async () => {
                try {
                  await adminApi.exportUsers();
                  toast.push({ type: 'success', message: 'Downloaded rbac-users.csv' });
                } catch (err: any) {
                  toast.push({ type: 'error', message: err?.message || 'Export failed' });
                }
              }}
            >
              Download CSV
            </Button>
            <Button type="secondary" onClick={() => navigate('/admin/users/bulk-import')}>
              Bulk import
            </Button>
            <Button type="primary" onClick={() => navigate('/admin/users/new')}>
              + Add user
            </Button>
          </>
        }
      />

      <Section>
      <FilterBar activeCount={activeCount} onClear={clearFilters}>
        <div style={{ minWidth: 240 }}>
          <SearchInput
            value={search}
            onChange={(next) => setParam('search', next || null)}
            placeholder="Search user_alias…"
            ariaLabel="Search users"
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <SearchInput
            value={group}
            onChange={(next) => setParam('group', next || null)}
            placeholder="Filter by group (exact)"
            ariaLabel="Filter by group"
          />
        </div>
      </FilterBar>

      {selected.size > 0 && (
        <Row alignmentVertical="center" spacing="medium">
          <Text type="b300">
            {selected.size} user{selected.size === 1 ? '' : 's'} selected
          </Text>
          <Button type="secondary" onClick={() => setPendingBulkDelete(true)}>
            Remove selected
          </Button>
          <Button type="tertiary" onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </Row>
      )}

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <Row alignmentHorizontal="center" spacingInset="500">
          <Loader type="circular" size="large" />
        </Row>
      ) : items.length === 0 ? (
        <Alert type="neutral">No users match the current filters.</Alert>
      ) : (
        <Table headerRows={1} showDividers={true} aria-label="User-group mappings">
          <TableRow>
            <TableCell width="40px"> </TableCell>
            <TableCell>User alias</TableCell>
            <TableCell>POSIX groups</TableCell>
            <TableCell>Last updated</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
          {items.map((u) => (
            <TableRow key={u.user_alias}>
              <TableCell>
                <Checkbox
                  checked={selected.has(u.user_alias)}
                  onChange={() => toggleSelected(u.user_alias)}
                >
                  <span className="visually-hidden">Select {u.user_alias}</span>
                </Checkbox>
              </TableCell>
              <TableCell>
                <Link to={`/admin/users/${encodeURIComponent(u.user_alias)}/edit`}>
                  {u.user_alias}
                </Link>
              </TableCell>
              <TableCell>
                <Text type="b200">{u.posix_groups.join(', ')}</Text>
              </TableCell>
              <TableCell>
                <Text type="b200">{u.last_updated?.slice(0, 19) || '-'}</Text>
              </TableCell>
              <TableCell>
                <Row spacing="xsmall">
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() =>
                      navigate(`/admin/users/${encodeURIComponent(u.user_alias)}/edit`)
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() => setPendingDelete(u.user_alias)}
                  >
                    Delete
                  </Button>
                </Row>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      )}

      <PaginationControls
        currentIndex={pageIndex}
        hasPrevious={pageIndex > 0}
        hasNext={!!nextToken}
        onPrevious={() => {
          // Clear the selection before moving pages. Otherwise checkboxes
          // from the current page would survive into the next one, where
          // they're invisible — a bulk-delete click after paginating
          // would destroy rows the admin can no longer see.
          setSelected(new Set());
          setPageIndex(pageIndex - 1);
        }}
        onNext={() => {
          if (!nextToken) return;
          const newTokens = [...tokens];
          newTokens[pageIndex + 1] = nextToken;
          setTokens(newTokens);
          setSelected(new Set());
          setPageIndex(pageIndex + 1);
        }}
        itemCount={items.length}
      />
      </Section>

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete user mapping ${pendingDelete || ''}?`}
        message="The user will lose all RBAC group assignments. They can be re-added later."
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={performDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingBulkDelete}
        title={`Delete ${selected.size} user mapping(s)?`}
        message={
          <Column spacing="xsmall">
            <Text>
              Every selected user loses their RBAC group assignments. Backend lockout
              protection will reject the request if it would leave zero admins.
            </Text>
            <Text type="b200" color="secondary">
              Affected aliases: {Array.from(selected).join(', ')}
            </Text>
          </Column>
        }
        confirmLabel="Delete selected"
        destructive
        busy={busy}
        onConfirm={performBulkDelete}
        onCancel={() => setPendingBulkDelete(false)}
      />
    </Column>
  );
};
